
import SmallHeaderComponent from "@/components/recorder/partials/SmallHeaderComponent.vue";
import FooterComponent from "@/components/recorder/partials/FooterComponent.vue";
import {defineComponent, PropType} from "vue";
import {Contribution} from "@/models/contribution";
import {Campaign, CollectUserData} from "@/models/campaign";
import {Helper} from "@/utils/helper";
import ConsentsCheckboxesComponent from "@/components/recorder/partials/ConsentsCheckboxesComponent.vue";

interface DetailsComponentData {
    termsAndConditionsConsentGiven: boolean;
    privacyPolicyAccepted: boolean;
    didClickContinue: boolean;
}

export default defineComponent({
    name: "DetailsComponent",
    components: {ConsentsCheckboxesComponent, FooterComponent, SmallHeaderComponent},
    props: {
        campaign: {
            type: Object as PropType<Campaign>,
            required: true
        },
        contribution: {
            type: Object as PropType<Contribution>,
            required: true
        },
    },
    data(): DetailsComponentData {
        return {
            termsAndConditionsConsentGiven: false,
            privacyPolicyAccepted: false,
            didClickContinue: false,
        };
    },
    mounted(): void {
        // don't override existing values
        if (this.campaign.collectEmails !== CollectUserData.Disabled && !this.contribution.email) {
            const email = Helper.getLocalStorageItem('contributor-email');
            if (email) {
                this.$emit('setEmail', email);
            }
        }

        if (this.campaign.collectNames !== CollectUserData.Disabled && !this.contribution.name) {
            const name = Helper.getLocalStorageItem('contributor-name');
            if (name) {
                this.$emit('setName', name);
            }
        }
    },

    methods: {
        // IMAGE
        onFileBrowseSelected(): void {
            if (!this.$refs.fileSelector) {
                console.error('no file selector!');
                return;
            }

            const fileSelector: HTMLInputElement = this.$refs.fileSelector as HTMLInputElement;
            const file = fileSelector && fileSelector.files ? fileSelector.files[0] : undefined;
            if (!file) {
                console.error('No image files selected');
                return;
            }
            this.readFile(file);
        },
        openImageSelector() {
            const fileSelector: HTMLInputElement = this.$refs.fileSelector as HTMLInputElement;
            fileSelector.click();
        },
        readFile(file: File) {
            const fileURLReader = new FileReader();
            fileURLReader.onload = (readEvent: ProgressEvent<FileReader>) => {
                if (readEvent.target && readEvent.target.result) {
                    this.$emit('setImageURL', readEvent.target.result);
                }
            };
            fileURLReader.readAsDataURL(file);

            const fileBlobReader = new FileReader();
            fileBlobReader.onload = (readEvent: ProgressEvent<FileReader>) => {
                if (readEvent.target && readEvent.target.result) {
                    this.$emit('setImageData', readEvent.target.result);
                }
            };
            fileBlobReader.readAsArrayBuffer(file);
        },
        removeSelectedImage() {
            this.$emit('setImageURL');
            this.$emit('setImageData');
        },

        tryToSubmit(): void {
            this.didClickContinue = true;

            if (this.canSubmit) {
                this.$emit('tryToSubmit');
            }
        },

        goToRecordingScreen() {
            this.$emit('goToRecordingScreen');
        },

        privacyPolicyAcceptedChanged(accepted: boolean) {
            this.privacyPolicyAccepted = accepted;
        },

        termsAndConditionsConsentGivenChanged(given: boolean) {
            this.termsAndConditionsConsentGiven = given;
        },
    },
    computed: {
        isCollectNamesDisabled(): boolean {
            return this.campaign.collectNames === CollectUserData.Disabled || !!this.$route.query.name;
        },
        isCollectNamesRequired(): boolean {
            return this.campaign.collectNames === CollectUserData.Required;
        },
        isCollectEmailsDisabled(): boolean {
            return this.campaign.collectEmails === CollectUserData.Disabled || !!this.$route.query.email;
        },
        isCollectEmailsRequired(): boolean {
            return this.campaign.collectEmails === CollectUserData.Required;
        },
        isCollectImagesDisabled(): boolean {
            return this.campaign.collectImages === CollectUserData.Disabled;
        },
        isCollectImagesRequired(): boolean {
            return this.campaign.collectImages === CollectUserData.Required;
        },

        name: {
            get(): string | undefined {
                return this.contribution.name;
            },
            set(name: string): void {
                this.$emit('setName', name);
            }
        },
        email: {
            get(): string | undefined {
                return this.contribution.email;
            },
            set(email: string): void {
                this.$emit('setEmail', email);
            }
        },

        collectingDataEnabled(): boolean {
            return !this.isCollectNamesDisabled || !this.isCollectEmailsDisabled || !this.isCollectImagesDisabled;
        },

        canSubmit(): boolean {
            if (this.campaign.enforcesTermsAndConditionsConsent && !this.termsAndConditionsConsentGiven) {
                return false;
            }

            if (this.campaign.enforcesPrivacyPolicyConsent && !this.privacyPolicyAccepted) {
                return false;
            }

            // name validation
            if (this.isCollectNamesRequired && !this.contribution.name) {
                return false;
            }

            // email validation
            if (this.isCollectEmailsRequired && !this.contribution.email) {
                return false;
            }

            if (this.contribution.email && !Helper.isValidEmail(this.contribution.email || '')) {
                return false;
            }

            // image validation
            if (this.isCollectImagesRequired && !this.contribution.imageData) {
                return false;
            }

            return true;
        },
    }
});
